import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  MenuItem,
  TextField,
  ListItemIcon,
  Typography,
  MenuList,
  Popper,
  Paper,
  ClickAwayListener,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowForwardIos, InfoOutlined } from '@mui/icons-material';
const AdvancedFilterMenu = ({
  triggerElement,
  mainItems,
  submenuItemsMap,
  onSelect,
  hideSubMenu,
  selectedOption,
  trackingSource
}) => {
  const theme = useTheme();
  // Stay above MUI Dialog/modal (e.g. Quick Filter), below snackbar/tooltip.
  const menuZIndex = (theme?.zIndex?.modal ?? 1300) + 1;

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMainItem, setActiveMainItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [currentSubMenuItem, setCurrentSubMenuItem] = useState(null);
  const hoverTimeout = useRef(null);

  const handleMainMenuOpen = (event) => {
    if(anchorEl===null) {
      setAnchorEl(event.currentTarget);
    } else {
      handleMainMenuClose();
    }
  };

  const handleMainMenuClose = () => {
    clearTimeout(hoverTimeout.current);
    setAnchorEl(null);
    setActiveMainItem(null);
    setSearchTerm('');
    setSubmenuAnchorEl(null); // 👈 keep this at the end
  };

  const filteredMainItems = mainItems.filter((mainItem) => {
    const isMainMatch = mainItem.label.toLowerCase().includes(searchTerm.toLowerCase());
    const subItems = submenuItemsMap[mainItem.label] || [];
    const matchingSubItems = subItems.filter((sub) =>
      sub.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return isMainMatch || matchingSubItems.length > 0;
  });

  useEffect(() => {
    clearTimeout(hoverTimeout.current);

    if (searchTerm.trim() === '') {
      setActiveMainItem(null);
      setSubmenuAnchorEl(null);
      return;
    }

    const match = mainItems.find((mainItem) => {
      const subItems = submenuItemsMap[mainItem.label] || [];
      // Side submenu only for multi-item groups
      if (subItems.length <= 1) return false;
      return subItems.some((sub) =>
        sub.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

    if (match) {
      hoverTimeout.current = setTimeout(() => {
        setActiveMainItem(match.label);
        setTimeout(() => {
          const el = document.querySelector(`[data-label="${match.label}"]`);
          if (el) {
            setSubmenuAnchorEl(el);
          } else {
            // fallback: keep Popper closed
            setSubmenuAnchorEl(null);
          }
        }, 0); // Wait for render
      }, 50);
    } else {
      setActiveMainItem(null);
      setSubmenuAnchorEl(null);
    }
  }, [searchTerm]);

  const getSourceImage = () => {

    let agencyIcon = "/bigatom-icon.svg";

    return agencyIcon;
};

const isSubmenuAnchorReady =
  submenuAnchorEl instanceof HTMLElement && document.body.contains(submenuAnchorEl);

useEffect(() => {
  return () => clearTimeout(hoverTimeout.current);
}, []);

  return (
    <>
      <Box
        onClick={(e) => {
          handleMainMenuOpen(e);
          setTimeout(() => {
            const input = document.querySelector('input[placeholder="Search"]');
            if (input) input.focus();
          }, 100);
        }}
      >
        {triggerElement}
      </Box>

      {Boolean(anchorEl) && (
        <ClickAwayListener onClickAway={handleMainMenuClose}>
          <Box sx={{ display: "flex", zIndex: menuZIndex }}>
            <Popper
              anchorEl={anchorEl}
              popperOptions={{
                placement: "bottom-start",
              }}
              modifiers={[
                {
                  name: "flip",
                  options: {
                    fallbackPlacements: [],
                  },
                },
              ]}
              sx={{
                zIndex: menuZIndex,
                maxHeight: "250px",
                minWidth: "200px",
                marginTop: "9px !important",
                position: "absolute",
              }}
              open
              MenuListProps={{
                sx: {
                  pointerEvents: "auto",
                  zIndex: menuZIndex,
                  position: "absolute",
                },
              }}
              PaperProps={{ sx: { pointerEvents: "auto" } }}
            >
              <Paper elevation={6}>
                <Box sx={{ padding: "8px 8px 0px 8px" }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    onFocus={() => setActiveMainItem(null)}
                    placeholder="Search"
                    fullWidth
                    value={searchTerm}
                    onKeyDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSearchTerm(e.target.value);
                    }}
                    inputProps={{
                      sx: {
                        padding: 0.5,
                        border: "1px solid rgba(0, 0, 0, 0.23)",
                        borderRadius: "4px",
                        fontSize: "12px",
                      },
                    }}
                  />
                </Box>
                <MenuList
                  onScroll={() => {
                    setActiveMainItem(null);
                  }}
                  sx={{
                    maxHeight: "250px",
                    overflowY: "auto",
                    maxWidth: "320px",
                    "&::-webkit-scrollbar": {
                      width: "6px",
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "transparent",
                      borderRadius: "6px",
                    },

                    /* Show scrollbar on hover or active scroll */
                    "&:hover::-webkit-scrollbar-thumb, &:active::-webkit-scrollbar-thumb":
                      {
                        background: "#b0b0b0", // visible scrollbar color
                      },
                  }}
                >
                  {filteredMainItems.map((item) => {
                    const sourceImage = getSourceImage();
                    const subItems = submenuItemsMap[item?.label] || [];
                    const submenuCount = subItems.length;
                    // Only multi-item groups open a side submenu; single-item groups select on click.
                    const hasExpandableSubmenu = submenuCount > 1;
                    const singleSubItem =
                      submenuCount === 1 ? subItems[0] : null;

                    return (
                      <React.Fragment key={item.label}>
                        <MenuItem
                          data-label={item.label}
                          onMouseEnter={(e) => {
                            clearTimeout(hoverTimeout.current);
                            if (hasExpandableSubmenu) {
                              const currentTarget = e.currentTarget;
                              hoverTimeout.current = setTimeout(() => {
                                setActiveMainItem(item.label);
                                setSubmenuAnchorEl(currentTarget); // Make sure we assign the DOM element
                              }, 50);
                            } else {
                              setActiveMainItem(null);
                              setSubmenuAnchorEl(null);
                            }
                          }}
                          onMouseLeave={() => {
                            clearTimeout(hoverTimeout.current);
                          }}
                          onClick={() => {
                            if (singleSubItem) {
                              onSelect(singleSubItem.label);
                              setActiveMainItem(null);
                              setSubmenuAnchorEl(null);
                              handleMainMenuClose();
                            } else if (!hasExpandableSubmenu) {
                              onSelect(item.label);
                              setActiveMainItem(null);
                              setSubmenuAnchorEl(null);
                              handleMainMenuClose();
                            }
                          }}
                          selected={
                            activeMainItem === item.label ||
                            (searchTerm !== "" &&
                              subItems.some((sub) =>
                                sub.label
                                  .toLowerCase()
                                  .includes(searchTerm.toLowerCase())
                              ))
                          }
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: "10px",
                              alignItems: "center",
                            }}
                          >
                            {item.source !== "N/A" &&
                              sourceImage &&
                              (Array?.isArray(sourceImage) ? (
                                <>
                                  {sourceImage?.map((img) => {
                                    return (
                                      <img
                                        key={img}
                                        src={img}
                                        width={12}
                                        height={12}
                                        alt=""
                                      />
                                    );
                                  })}
                                </>
                              ) : (
                                <img
                                  src={sourceImage}
                                  width={12}
                                  height={12}
                                  alt=""
                                />
                              ))}
                            <Typography
                              fontSize={12}
                              sx={{
                                color:
                                  activeMainItem === item.label
                                    ? "#6148F6"
                                    : "",
                                textWrap: "wrap",
                                wordBreak: "break-word",
                              }}
                              variant="subtitle2"
                            >
                              {item?.symbol ? <><span style={{fontSize:".9rem", marginRight:".8rem"}}>{item.symbol}</span> <span>{item.label}</span></>: item.label}
                            
                            </Typography>
                          </Box>
                          {hasExpandableSubmenu &&
                            activeMainItem === item.label &&
                            searchTerm === "" && (
                              <ListItemIcon sx={{ justifyContent: "end" }}>
                                <ArrowForwardIos fontSize="12px" />
                              </ListItemIcon>
                            )}
                        </MenuItem>
                        {/* Inline submenu rendering when searchTerm is not blank */}
                        {searchTerm !== "" &&
                          submenuCount > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                pl: 0,
                              }}
                            >
                              {submenuItemsMap[item.label]
                                .filter((subItem) =>
                                  subItem.label
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase())
                                )
                                .map((subItem) => {
                                  const image = getSourceImage();
                                  const description = subItem?.description
                                    ? subItem?.description?.replace(
                                        /\$(OverallAdSpend|OverallAdRevenue|WebsiteRevenue|FbAdSpend|GoogleAdSpend)/g,
                                        ""
                                      )
                                    : "";

                                  return (
                                    <MenuItem
                                      key={subItem.label}
                                      onClick={() => {
                                        onSelect(subItem.label);
                                        handleMainMenuClose();
                                      }}
                                      selected={
                                        selectedOption?.category ===
                                        subItem?.label
                                      }
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        pl: 5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: "10px",
                                          alignItems: "center",
                                        }}
                                      >
                                        {subItem.source !== "N/A" &&
                                          image &&
                                          (Array.isArray(image) ? (
                                            image.map((img) => (
                                              <img
                                                key={img}
                                                src={img}
                                                width={12}
                                                height={12}
                                                alt=""
                                              />
                                            ))
                                          ) : (
                                            <img
                                              src={image}
                                              width={12}
                                              height={12}
                                              alt=""
                                            />
                                          ))}
                                        <Typography
                                          fontSize={12}
                                          variant="subtitle2"
                                          sx={{
                                            color:
                                              selectedOption?.category ===
                                              subItem.label
                                                ? "#6148F6"
                                                : "",
                                          }}
                                        >
                                          {subItem.label}
                                        </Typography>
                                      </Box>
                                      {description && (
                                        <ListItemIcon
                                          sx={{ justifyContent: "end" }}
                                        >
                                          <Tooltip
                                            disableInteractive
                                            title={description}
                                          >
                                            <InfoOutlined fontSize="12px" />
                                          </Tooltip>
                                        </ListItemIcon>
                                      )}
                                    </MenuItem>
                                  );
                                })}
                            </Box>
                          )}
                      </React.Fragment>
                    );
                  })}
                </MenuList>
              </Paper>
            </Popper>

            {!hideSubMenu &&
              searchTerm === "" &&
              isSubmenuAnchorReady &&
              Array.isArray(submenuItemsMap[activeMainItem]) &&
              submenuItemsMap[activeMainItem]?.length > 1 && (
                <Popper
                  open
                  anchorEl={submenuAnchorEl}
                  placement="right-start"
                  modifiers={[
                    {
                      name: "flip",
                      options: {
                        fallbackPlacements: [],
                      },
                    },
                  ]}
                  sx={{
                    zIndex: menuZIndex,
                    marginLeft: "0.75rem !important",
                    pointerEvents: "auto",
                  }}
                >
                  <Paper elevation={6}>
                    <MenuList
                      sx={{ py: 1, maxHeight: "300px", overflowY: "auto" }}
                    >
                      {submenuItemsMap[activeMainItem]
                        ?.filter(
                          (subItem) =>
                            searchTerm === "" ||
                            subItem.label
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                        )
                        .map((subItem) => {
                          const image = getSourceImage();
                          const description = subItem?.description
                            ? subItem?.description?.replace(
                                /\$(OverallAdSpend|OverallAdRevenue|WebsiteRevenue|FbAdSpend|GoogleAdSpend)/g,
                                ""
                              )
                            : "";
                          return (
                            <MenuItem
                              key={subItem.label}
                              onMouseEnter={() =>
                                setCurrentSubMenuItem(subItem?.label)
                              }
                              onClick={() => {
                                onSelect(subItem.label);
                                handleMainMenuClose();
                              }}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                              selected={
                                selectedOption?.category === subItem?.label ||
                                currentSubMenuItem === subItem?.label
                              }
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "10px",
                                  alignItems: "center",
                                }}
                              >
                                {subItem.source !== "N/A" &&
                                  image &&
                                  (Array?.isArray(image) ? (
                                    <>
                                      {image?.map((img) => {
                                        return (
                                          <img
                                            key={img}
                                            src={img}
                                            width={12}
                                            height={12}
                                            alt=""
                                          />
                                        );
                                      })}
                                    </>
                                  ) : (
                                    <img
                                      src={image}
                                      width={12}
                                      height={12}
                                      alt=""
                                    />
                                  ))}
                                <Typography
                                  fontSize={12}
                                  variant="subtitle2"
                                  sx={{
                                    color:
                                      selectedOption?.category === subItem.label
                                        ? "#6148F6"
                                        : "",
                                  }}
                                >
                                  {subItem.label}
                                </Typography>
                              </Box>
                              {description ? (
                                <ListItemIcon sx={{ justifyContent: "end" }}>
                                  <Tooltip
                                    disableInteractive
                                    title={description ? description : ""}
                                  >
                                    <InfoOutlined fontSize="12px" />
                                  </Tooltip>
                                </ListItemIcon>
                              ) : null}
                            </MenuItem>
                          );
                        })}
                    </MenuList>
                  </Paper>
                </Popper>
              )}
          </Box>
        </ClickAwayListener>
      )}
    </>
  );
};

export default AdvancedFilterMenu;


